<template>
  <div class="loans-manager">
    <v-container fluid class="py-6">

      <!-- HEADER SECTION -->
      <v-row class="mb-4">
        <v-col cols="12">
          <v-card class="header-card" elevation="3">
            <v-card-text>
              <v-row align="center">
                <v-col cols="auto">
                  <div class="d-flex align-center">
                    <v-avatar color="primary" size="48" class="mr-3">
                      <v-icon dark size="28">mdi-cash-multiple</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-h5 font-weight-bold primary--text">Loan Manager</div>
                      <div class="text-caption grey--text">Review & Process Applications</div>
                    </div>
                  </div>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto">
                  <v-chip color="warning" outlined small class="mr-3">
                    <v-icon left small>mdi-clock-alert-outline</v-icon>
                    {{ pendingCount }} Pending
                  </v-chip>
                  <v-chip color="grey" outlined small class="mr-3">
                    <v-icon left small>mdi-history</v-icon>
                    {{ legacyCount }} Legacy
                  </v-chip>
                  <v-btn icon @click="fetchLoans" :loading="loading">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- STATISTICS CARDS -->
      <v-row class="mb-4">
        <v-col v-for="stat in stats" :key="stat.label" cols="12" sm="6" md="3">
          <v-card class="stat-card" elevation="2" :color="stat.bg">
            <v-card-text>
              <div class="d-flex justify-space-between align-start">
                <div>
                  <div class="stat-label mb-1">{{ stat.label }}</div>
                  <div class="stat-value" :style="{ color: stat.color }">{{ stat.value }}</div>
                </div>
                <v-avatar :color="stat.color" size="48" class="elevation-3">
                  <v-icon dark size="24">{{ stat.icon }}</v-icon>
                </v-avatar>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- FILTERS AND SEARCH -->
      <v-card class="mb-4" elevation="2">
        <v-card-text class="pb-0">
          <v-row align="center">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="search"
                placeholder="Search by username, amount, phone number..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                hide-details
                clearable
                @input="debouncedSearch"
                class="search-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="filterStatus"
                :items="tabs"
                item-text="label"
                item-value="value"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-filter-variant"
              >
                <template v-slot:selection="{ item }">
                  <v-badge
                    v-if="item.count > 0"
                    :content="item.count"
                    color="error"
                    inline
                  >
                    {{ item.label }}
                  </v-badge>
                  <span v-else>{{ item.label }}</span>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="filterSystemType"
                :items="systemTypeFilters"
                item-text="label"
                item-value="value"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-filter-variant"
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- LOANS GRID -->
      <v-row v-if="!loading && filteredLoans.length > 0">
        <v-col
          v-for="loan in paginatedLoans"
          :key="loan.id"
          cols="12"
          md="6"
          lg="4"
        >
          <v-card
            class="loan-card"
            elevation="3"
            @click="openDetail(loan)"
            :class="{
              'pending-pulse': getLoanStatus(loan) === 'PENDING' && !loan.is_legacy,
              'legacy-card': loan.is_legacy
            }"
          >
            <!-- Badge Legacy -->
            <v-chip
              v-if="loan.is_legacy"
              x-small
              color="grey"
              text-color="white"
              class="legacy-badge"
            >
              <v-icon left x-small>mdi-history</v-icon>
              Ancien système
            </v-chip>

            <!-- Card Header -->
            <v-card-title class="card-header pb-2">
              <div class="d-flex align-center w-100">
                <v-avatar :color="getStatusColor(loan)" size="48" class="mr-3">
                  <span class="white--text text-h6">{{ getInitial(loan.client_username) }}</span>
                </v-avatar>
                <div class="flex-grow-1">
                  <div class="font-weight-bold">
                    {{ getLoanDisplayName(loan) }}
                  </div>
                  <div class="caption grey--text">ID: #{{ loan.id }}</div>
                </div>
                <v-chip
                  x-small
                  :color="getStatusConfigFromLoan(loan).bg"
                  :text-color="getStatusConfigFromLoan(loan).color"
                  class="status-chip"
                >
                  <v-icon left x-small>{{ getStatusConfigFromLoan(loan).icon }}</v-icon>
                  {{ getStatusConfigFromLoan(loan).label }}
                </v-chip>
              </div>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Card Body -->
            <v-card-text class="pb-2">
              <!-- NOUVEAU SYSTÈME -->
              <div v-if="!loan.is_legacy" class="amount-section mb-3">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="text-caption grey--text">Principal</span>
                  <span class="text-h6 font-weight-bold primary--text">
                    {{ formatAmount(loan.loan_amount) }} FCFA
                  </span>
                </div>
                <div class="d-flex justify-space-between align-center">
                  <span class="text-caption grey--text">Interest</span>
                  <span class="text-body-2 font-weight-medium orange--text">
                    +{{ formatAmount(loan.interest_amount) }} FCFA
                  </span>
                </div>
                <v-divider class="my-2"></v-divider>
                <div class="d-flex justify-space-between align-center">
                  <span class="text-caption grey--text">Total Due</span>
                  <span class="text-h6 font-weight-black success--text">
                    {{ formatAmount(loan.total_amount) }} FCFA
                  </span>
                </div>
              </div>

              <!-- ANCIEN SYSTÈME (LEGACY) -->
              <div v-else class="legacy-section mb-3">
                <v-alert
                  dense
                  text
                  color="grey"
                  icon="mdi-information-outline"
                  class="mb-2"
                >
                  Legacy Loan System
                </v-alert>

                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="text-caption grey--text">Type</span>
                  <span class="text-body-2 font-weight-medium">
                    {{ getLegacyType(loan) }}
                  </span>
                </div>

                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="text-caption grey--text">Target Amount</span>
                  <span class="text-h6 font-weight-bold primary--text">
                    {{ formatAmount(getLegacyTargetAmount(loan)) }} FCFA
                  </span>
                </div>

                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="text-caption grey--text">Saved Amount</span>
                  <span class="text-body-2 font-weight-medium success--text">
                    {{ formatAmount(getLegacySavedAmount(loan)) }} FCFA
                  </span>
                </div>

                <v-divider class="my-2"></v-divider>

                <div class="d-flex justify-space-between align-center">
                  <span class="text-caption grey--text">Progress</span>
                  <v-chip x-small color="info" text-color="white">
                    {{ getLegacyPercentage(loan) }}
                  </v-chip>
                </div>

                <v-progress-linear
                  :value="parsePercentage(getLegacyPercentage(loan))"
                  color="primary"
                  height="6"
                  rounded
                  class="mt-2"
                ></v-progress-linear>
              </div>

              <!-- Details Grid -->
              <v-row dense class="info-grid">
                <v-col cols="6">
                  <div class="info-item">
                    <v-icon small color="grey">{{ loan.is_legacy ? 'mdi-card-account-details' : 'mdi-phone' }}</v-icon>
                    <span class="ml-1">{{ getContactInfo(loan) }}</span>
                  </div>
                </v-col>
                <v-col cols="6" v-if="!loan.is_legacy">
                  <div class="info-item">
                    <v-chip
                      x-small
                      :color="loan.operator === 'MTN' ? '#ffcc00' : '#ff6600'"
                      :text-color="loan.operator === 'MTN' ? '#000' : '#fff'"
                      class="font-weight-bold"
                    >
                      {{ loan.operator }}
                    </v-chip>
                  </div>
                </v-col>
                <v-col cols="6" v-if="!loan.is_legacy">
                  <div class="info-item">
                    <v-icon small color="grey">mdi-calendar-clock</v-icon>
                    <span class="ml-1">{{ loan.repayment_period_days }}d</span>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="info-item">
                    <v-icon small color="grey">mdi-clock-outline</v-icon>
                    <span class="ml-1 caption">{{ formatDateShort(loan.application_date || loan.creation_date) }}</span>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Card Actions -->
            <v-card-actions class="pa-3">
              <!-- Actions pour NOUVEAU système uniquement -->
              <template v-if="!loan.is_legacy">
                <v-btn
                  v-if="getLoanStatus(loan) === 'PENDING'"
                  small
                  color="success"
                  dark
                  @click.stop="quickAction(loan, 'APPROVED')"
                >
                  <v-icon left small>mdi-check-circle</v-icon>
                  Approve
                </v-btn>
                <v-btn
                  v-if="getLoanStatus(loan) === 'PENDING'"
                  small
                  color="error"
                  outlined
                  @click.stop="quickAction(loan, 'REJECTED')"
                >
                  <v-icon left small>mdi-cancel</v-icon>
                  Reject
                </v-btn>

              </template>

              <!-- Badge pour LEGACY système -->
              <v-chip
                v-else
                small
                color="grey"
                outlined
              >
                <v-icon left small>mdi-lock</v-icon>
                Legacy - View Only
              </v-chip>

              <v-spacer></v-spacer>
              <v-btn
                small
                icon
                color="primary"
                @click.stop="openDetail(loan)"
              >
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- LOADING STATE -->
      <v-row v-if="loading">
        <v-col cols="12" class="text-center py-12">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          ></v-progress-circular>
          <div class="mt-4 text-h6 grey--text">Loading loans...</div>
        </v-col>
      </v-row>

      <!-- EMPTY STATE -->
      <v-row v-if="!loading && filteredLoans.length === 0">
        <v-col cols="12">
          <v-card class="text-center py-12" elevation="0" color="grey lighten-5">
            <v-icon size="80" color="grey lighten-1">mdi-file-document-outline</v-icon>
            <div class="text-h6 grey--text mt-4">No loans found</div>
            <div class="grey--text">Try adjusting your filters or search query</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- PAGINATION -->
      <v-row v-if="!loading && filteredLoans.length > itemsPerPage" class="mt-4">
        <v-col cols="12" class="text-center">
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="7"
            color="primary"
          ></v-pagination>
        </v-col>
      </v-row>

    </v-container>

    <!-- DETAIL DRAWER -->
    <v-navigation-drawer
      v-model="detailDrawer"
      right
      fixed
      temporary
      width="500"
      class="detail-drawer"
    >
      <div v-if="selectedLoan">
        <!-- Drawer Header -->
        <div class="drawer-header pa-6 white--text">
          <v-btn icon dark absolute top right @click="detailDrawer = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <div class="d-flex align-center mb-4">
            <v-avatar size="64" color="white">
              <span class="primary--text text-h4 font-weight-bold">
                {{ getInitial(selectedLoan.client_username) }}
              </span>
            </v-avatar>
            <div class="ml-4">
              <div class="text-h5 font-weight-bold">
                {{ getLoanDisplayName(selectedLoan) }}
              </div>
              <div class="text-caption">Loan #{{ selectedLoan.id }}</div>
            </div>
          </div>

          <!-- Status + Legacy Badge -->
          <div class="d-flex" style="gap: 8px;">
            <v-chip
              :color="getStatusConfigFromLoan(selectedLoan).bg"
              :text-color="getStatusConfigFromLoan(selectedLoan).color"
              label
              large
            >
              <v-icon left>{{ getStatusConfigFromLoan(selectedLoan).icon }}</v-icon>
              {{ getStatusConfigFromLoan(selectedLoan).label }}
            </v-chip>

            <v-chip
              v-if="selectedLoan.is_legacy"
              color="grey"
              text-color="white"
              label
              large
            >
              <v-icon left>mdi-history</v-icon>
              Ancien système
            </v-chip>
          </div>
        </div>

        <!-- Drawer Content -->
        <v-container class="pa-6">

          <!-- NOUVEAU SYSTÈME - Financial Summary -->
          <v-card v-if="!selectedLoan.is_legacy" class="mb-4" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
              <v-icon left color="primary">mdi-cash-multiple</v-icon>
              Financial Details
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="grey--text">Principal Amount</td>
                    <td class="text-right font-weight-bold">{{ formatAmount(selectedLoan.loan_amount) }} FCFA</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Interest Amount</td>
                    <td class="text-right font-weight-bold orange--text">{{ formatAmount(selectedLoan.interest_amount) }} FCFA</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Total Repayment</td>
                    <td class="text-right font-weight-bold success--text">{{ formatAmount(selectedLoan.total_amount) }} FCFA</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Repayment Period</td>
                    <td class="text-right font-weight-bold">{{ selectedLoan.repayment_period_days }} days</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Due Date</td>
                    <td class="text-right font-weight-bold">{{ selectedLoan.due_date || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Commission</td>
                    <td class="text-right font-weight-bold">{{ formatAmount(selectedLoan.referrer_commission) }} FCFA</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <!-- LEGACY SYSTÈME - Financial Summary -->
          <v-card v-else class="mb-4" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
              <v-icon left color="grey">mdi-cash-multiple</v-icon>
              Legacy Financial Details
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="grey--text">Type</td>
                    <td class="text-right font-weight-bold">{{ getLegacyType(selectedLoan) }}</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Target Amount</td>
                    <td class="text-right font-weight-bold primary--text">{{ formatAmount(getLegacyTargetAmount(selectedLoan)) }} FCFA</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Saved Amount</td>
                    <td class="text-right font-weight-bold success--text">{{ formatAmount(getLegacySavedAmount(selectedLoan)) }} FCFA</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Progress</td>
                    <td class="text-right font-weight-bold">{{ getLegacyPercentage(selectedLoan) }}</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Target Date</td>
                    <td class="text-right font-weight-bold">{{ getLegacyTargetDate(selectedLoan) }}</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Interest</td>
                    <td class="text-right font-weight-bold">{{ formatAmount(getLegacyInterest(selectedLoan)) }} FCFA</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Days Remaining</td>
                    <td class="text-right font-weight-bold">{{ getLegacyRemainingDays(selectedLoan) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <!-- Client Information -->
          <v-card class="mb-4" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
              <v-icon left color="primary">mdi-account</v-icon>
              Client Information
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="info-row">
                <span class="grey--text">User ID</span>
                <span class="font-weight-medium">{{ selectedLoan.client }}</span>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="info-row">
                <span class="grey--text">Username</span>
                <span class="font-weight-medium">{{ selectedLoan.client_username }}</span>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="info-row">
                <span class="grey--text">Email</span>
                <span class="font-weight-medium">{{ selectedLoan.client_email || '—' }}</span>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="info-row" v-if="!selectedLoan.is_legacy">
                <span class="grey--text">Phone Number</span>
                <span class="font-weight-medium">{{ selectedLoan.phone_number || '—' }}</span>
              </div>
              <div class="info-row" v-if="!selectedLoan.is_legacy">
                <span class="grey--text">Operator</span>
                <v-chip
                  x-small
                  :color="selectedLoan.operator === 'MTN' ? '#ffcc00' : '#ff6600'"
                  :text-color="selectedLoan.operator === 'MTN' ? '#000' : '#fff'"
                  class="font-weight-bold"
                >
                  {{ selectedLoan.operator }}
                </v-chip>
              </div>
              <div class="info-row" v-if="selectedLoan.is_legacy">
                <span class="grey--text">Transaction Operator</span>
                <span class="font-weight-medium">{{ selectedLoan.transaction_operator || '—' }}</span>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="info-row">
                <span class="grey--text">Application Date</span>
                <span class="font-weight-medium">{{ formatDate(selectedLoan.application_date || selectedLoan.creation_date) }}</span>
              </div>
            </v-card-text>
          </v-card>

          <!-- Eligibility Criteria (Nouveau système uniquement) -->
          <v-card v-if="!selectedLoan.is_legacy" class="mb-4" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
              <v-icon left color="primary">mdi-shield-check</v-icon>
              Eligibility Criteria
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="mb-3">
                <v-chip
                  v-for="(c, i) in getEligibilityCriteria(selectedLoan)"
                  :key="i"
                  small
                  color="primary"
                  outlined
                  class="ma-1"
                >
                  <v-icon left x-small>mdi-check</v-icon>
                  {{ formatCriterion(c) }}
                </v-chip>
              </div>
              <v-divider class="my-3"></v-divider>
              <div class="info-row">
                <span class="grey--text">Max Eligible</span>
                <span class="font-weight-bold success--text">{{ formatAmount(selectedLoan.max_eligible_amount) }} FCFA</span>
              </div>
              <div class="info-row mt-2">
                <span class="grey--text">Requested</span>
                <span class="font-weight-bold">{{ formatAmount(selectedLoan.loan_amount) }} FCFA</span>
              </div>
            </v-card-text>
          </v-card>

          <!-- Legacy Description -->
          <v-card v-if="selectedLoan.is_legacy && getLegacyDescription(selectedLoan)" class="mb-4" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
              <v-icon left color="grey">mdi-text-box</v-icon>
              Description
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="purpose-text">{{ getLegacyDescription(selectedLoan) }}</div>
            </v-card-text>
          </v-card>

          <!-- Purpose (Nouveau système) -->
          <v-card v-if="!selectedLoan.is_legacy && selectedLoan.purpose" class="mb-4" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
              <v-icon left color="primary">mdi-text-box</v-icon>
              Loan Purpose
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="purpose-text">{{ selectedLoan.purpose }}</div>
            </v-card-text>
          </v-card>

          <!-- Manager Note (Nouveau système uniquement) -->
          <v-card
            v-if="!selectedLoan.is_legacy && isActionableStatus(getLoanStatus(selectedLoan))"
            class="mb-4"
            elevation="2"
          >
            <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
              <v-icon left color="primary">mdi-note-edit</v-icon>
              Manager Note
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-textarea
                v-model="managerNote"
                outlined
                rows="3"
                placeholder="Add a note for this decision (optional)..."
                hide-details
              ></v-textarea>
            </v-card-text>
          </v-card>

          <!-- Action Buttons (Nouveau système uniquement) -->
          <div v-if="!selectedLoan.is_legacy" class="action-buttons">
            <v-btn
              v-if="getLoanStatus(selectedLoan) === 'PENDING'"
              color="success"
              large
              block
              dark
              class="mb-3"
              :loading="actionLoading"
              @click="processAction('APPROVED')"
            >
              <v-icon left>mdi-check-circle</v-icon>
              Approve Loan
            </v-btn>
            <v-btn
              v-if="getLoanStatus(selectedLoan) === 'PENDING'"
              color="error"
              large
              block
              outlined
              :loading="actionLoading"
              @click="processAction('REJECTED')"
            >
              <v-icon left>mdi-cancel</v-icon>
              Reject Application
            </v-btn>

            <v-btn
              v-if="getLoanStatus(selectedLoan) === 'APPROVED'"
              color="primary"
              large
              block
              dark
              :loading="actionLoading"
              @click="processAction('DISBURSED')"
            >
              <v-icon left>mdi-send</v-icon>
              Mark as Disbursed
            </v-btn>

            <v-btn
              v-if="getLoanStatus(selectedLoan) === 'REPAYING'"
              color="success"
              large
              block
              outlined
              :loading="actionLoading"
              @click="processAction('COMPLETED')"
            >
              <v-icon left>mdi-check-all</v-icon>
              Mark as Completed
            </v-btn>
          </div>

          <!-- Legacy Info Badge -->
          <v-alert
            v-else
            type="info"
            text
            border="left"
            class="mb-0"
          >
            <div class="d-flex align-center">
              <v-icon left>mdi-information</v-icon>
              <div>
                <strong>Legacy Loan</strong>
                <div class="text-caption">This loan is from the old system and cannot be modified through this interface.</div>
              </div>
            </div>
          </v-alert>

        </v-container>
      </div>
    </v-navigation-drawer>

    <!-- CONFIRMATION DIALOG -->
    <v-dialog v-model="confirmDialog" max-width="450" persistent>
      <v-card>
        <v-card-title
          :class="['pa-6 white--text', confirmAction === 'REJECTED' ? 'error' : 'success']"
        >
          <v-icon large color="white" class="mr-3">{{ confirmActionIcon }}</v-icon>
          <span>{{ confirmActionTitle }}</span>
        </v-card-title>
        <v-card-text class="pa-6">
          <div class="text-body-1">
            Are you sure you want to <strong>{{ confirmActionTitle.toLowerCase() }}</strong>
            this loan?
          </div>
          <v-card v-if="confirmLoan" class="mt-4 pa-4 grey lighten-5" elevation="0">
            <div class="d-flex justify-space-between mb-2">
              <span class="grey--text">Client:</span>
              <span class="font-weight-bold">{{ confirmLoan.client_username }}</span>
            </div>
            <div class="d-flex justify-space-between">
              <span class="grey--text">Amount:</span>
              <span class="font-weight-bold primary--text">
                {{ formatAmount(confirmLoan.loan_amount || confirmLoan.target_amount) }} FCFA
              </span>
            </div>
          </v-card>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="confirmDialog = false"
            :disabled="actionLoading"
          >
            Cancel
          </v-btn>
          <v-btn
            :color="confirmAction === 'REJECTED' ? 'error' : 'success'"
            dark
            @click="confirmProcess"
            :loading="actionLoading"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'LoanManagerDashboard',

  data() {
    return {
      loading: false,
      isFetching: false,
      actionLoading: false,
      initialLoadDone: false,
      hasError: false,

      loans: [],
      filteredLoans: [],

      search: '',
      filterStatus: 'ALL',
      filterSystemType: 'NEW',

      abortController: null,

      detailDrawer: false,
      selectedLoan: null,
      managerNote: '',
      confirmDialog: false,
      confirmAction: '',
      confirmLoan: null,

      currentPage: 1,
      itemsPerPage: 9,

      searchTimeout: null,

      tabs: [
        { label: 'All Loans', value: 'ALL', count: 0 },
        { label: 'Pending', value: 'PENDING', count: 0 },
        { label: 'Approved', value: 'APPROVED', count: 0 },
        { label: 'Disbursed', value: 'DISBURSED', count: 0 },
        { label: 'Repaying', value: 'REPAYING', count: 0 },
        { label: 'Completed', value: 'COMPLETED', count: 0 },
        { label: 'Rejected', value: 'REJECTED', count: 0 },
      ],

      systemTypeFilters: [
        { label: 'All Systems', value: 'ALL' },
        { label: 'New System Only', value: 'NEW' },
        { label: 'Legacy Only', value: 'LEGACY' },
      ],

      statusConfig: {
        PENDING: { label: 'Pending', color: '#e65100', bg: '#fff3e0', icon: 'mdi-clock-alert' },
        APPROVED: { label: 'Approved', color: '#1b5e20', bg: '#e8f5e9', icon: 'mdi-check-circle' },
        DISBURSED: { label: 'Disbursed', color: '#0d47a1', bg: '#e3f2fd', icon: 'mdi-send' },
        REPAYING: { label: 'Repaying', color: '#4a148c', bg: '#f3e5f5', icon: 'mdi-autorenew' },
        COMPLETED: { label: 'Completed', color: '#1b5e20', bg: '#c8e6c9', icon: 'mdi-check-all' },
        REJECTED: { label: 'Rejected', color: '#b71c1c', bg: '#ffebee', icon: 'mdi-cancel' },
        DEFAULTED: { label: 'Defaulted', color: '#b71c1c', bg: '#ffcdd2', icon: 'mdi-alert' },
      },
    }
  },

  computed: {
    pendingCount() {
      return Array.isArray(this.loans)
        ? this.loans.filter(l => !l.is_legacy && this.getLoanStatus(l) === 'PENDING').length
        : 0
    },

    legacyCount() {
      return Array.isArray(this.loans) ? this.loans.filter(l => l.is_legacy).length : 0
    },

    stats() {
      const loans = Array.isArray(this.loans) ? this.loans : []
      const newLoans = loans.filter(l => !l.is_legacy)
      const total = newLoans.length
      const pending = newLoans.filter(l => this.getLoanStatus(l) === 'PENDING').length
      const active = newLoans.filter(l => ['APPROVED', 'DISBURSED', 'REPAYING'].includes(this.getLoanStatus(l))).length
      const volume = newLoans.reduce((s, l) => s + (Number(l.loan_amount) || 0), 0)

      return [
        { label: 'New Applications', value: total, icon: 'mdi-file-document-multiple', bg: '#e3f2fd', color: '#1976d2' },
        { label: 'Pending Review', value: pending, icon: 'mdi-clock-alert-outline', bg: '#fff3e0', color: '#f57c00' },
        { label: 'Active Loans', value: active, icon: 'mdi-cash-check', bg: '#e8f5e9', color: '#388e3c' },
        { label: 'Total Volume', value: this.formatAmount(volume) + ' F', icon: 'mdi-chart-line', bg: '#f3e5f5', color: '#7b1fa2' },
      ]
    },

    paginatedLoans() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredLoans.slice(start, end)
    },

    totalPages() {
      return Math.ceil(this.filteredLoans.length / this.itemsPerPage)
    },

    confirmActionTitle() {
      const titles = {
        APPROVED: 'Approve Loan',
        REJECTED: 'Reject Application',
        DISBURSED: 'Mark Disbursed',
        COMPLETED: 'Mark Completed'
      }
      return titles[this.confirmAction] || this.confirmAction
    },

    confirmActionIcon() {
      const icons = {
        APPROVED: 'mdi-check-circle',
        REJECTED: 'mdi-cancel',
        DISBURSED: 'mdi-send',
        COMPLETED: 'mdi-check-all'
      }
      return icons[this.confirmAction] || 'mdi-help'
    }
  },

  watch: {
    filterStatus() {
      if (this.initialLoadDone) this.filterLoansLocally()
    },
    filterSystemType() {
      if (this.initialLoadDone) this.filterLoansLocally()
    },
    loans: {
      handler() {
        if (this.initialLoadDone) {
          this.updateTabCounts()
          this.filterLoansLocally()
        }
      },
      deep: true
    },
    currentPage() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  },

  mounted() {
    this.fetchLoans()
  },

  beforeDestroy() {
    if (this.abortController) this.abortController.abort()
    clearTimeout(this.searchTimeout)
  },

  methods: {
    // ========== STATUT ==========
    getLoanStatus(loan) {
      if (!loan) return 'UNKNOWN'
      return loan.status_display?.value || 'UNKNOWN'
    },

    getStatusConfigFromLoan(loan) {
      const statusValue = this.getLoanStatus(loan)
      return this.statusConfig[statusValue] || {
        label: statusValue || 'Unknown',
        color: '#757575',
        bg: '#f5f5f5',
        icon: 'mdi-help-circle'
      }
    },

    getStatusColor(loan) {
      return this.getStatusConfigFromLoan(loan).color
    },

    isActionableStatus(status) {
      return ['PENDING', 'APPROVED'].includes(status)
    },

    // ========== AFFICHAGE PRÊT ==========
    getLoanDisplayName(loan) {
      if (loan.is_legacy) {
        return loan.name || 'Legacy Loan'
      }
      return loan.client_username || 'Unknown'
    },

    getInitial(username) {
      if (!username || typeof username !== 'string') return '?'
      return username.charAt(0).toUpperCase()
    },

    // ========== CHAMPS LEGACY ==========
    getLegacyType(loan) {
      // On peut déduire le type : si target_date et target_amount → 'Date & Amount'
      if (loan.target_date && loan.target_amount) return 'Date & Amount'
      if (loan.target_amount) return 'Amount'
      if (loan.target_date) return 'Date'
      return 'Standard'
    },

    getLegacyTargetAmount(loan) {
      return loan.target_amount || 0
    },

    getLegacySavedAmount(loan) {
      return loan.saved_amount || 0
    },

    getLegacyPercentage(loan) {
      return loan.percentage_completed || '0%'
    },

    getLegacyTargetDate(loan) {
      return loan.target_date || '—'
    },

    getLegacyInterest(loan) {
      return loan.interest || 0
    },

    getLegacyRemainingDays(loan) {
      if (loan.target_date) {
        const today = new Date()
        const target = new Date(loan.target_date)
        const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24))
        return diff > 0 ? diff + ' jours' : 'Échu'
      }
      return '—'
    },

    getLegacyDescription(loan) {
      // Si le modèle legacy avait une description, tu peux la retourner ici
      return null // ou loan.description si existant
    },

    getContactInfo(loan) {
      if (loan.is_legacy) {
        return loan.transaction_operator || '—'
      }
      return loan.phone_number || '—'
    },

    // ========== CHAMPS NOUVEAU SYSTÈME ==========
    getEligibilityCriteria(loan) {
      return loan.eligibility_criteria || []
    },

    // ========== CHARGEMENT DES DONNÉES ==========
    async fetchLoans() {
      if (this.isFetching) return
      this.isFetching = true
      this.loading = true
      this.hasError = false

      if (this.abortController) this.abortController.abort()
      this.abortController = new AbortController()

      try {
        console.log('📡 Chargement des prêts depuis API...')
        const response = await axios.get('/api/v1/manager/loans/manager/', {
          signal: this.abortController.signal
        })
        this.loans = response.data || []
        if (!Array.isArray(this.loans)) {
          console.error('Les données reçues ne sont pas un tableau :', this.loans)
          this.loans = []
        }
        console.log('✅ ' + this.loans.length + ' prêts chargés (' + this.legacyCount + ' legacy)')
        this.initialLoadDone = true
        this.updateTabCounts()
        this.filterLoansLocally()
      } catch (e) {
        if (e.name !== 'AbortError' && e.code !== 'ERR_CANCELED') {
          console.error('Erreur chargement prêts :', e)
          this.hasError = true
          this.notify('Échec du chargement des prêts', 'error')
        }
      } finally {
        this.isFetching = false
        this.loading = false
        this.abortController = null
      }
    },

    filterLoansLocally() {
      if (!Array.isArray(this.loans)) {
        this.filteredLoans = []
        return
      }

      try {
        let list = this.loans.slice()

        // Filtre par type de système
        if (this.filterSystemType === 'NEW') {
          list = list.filter(l => !l.is_legacy)
        } else if (this.filterSystemType === 'LEGACY') {
          list = list.filter(l => l.is_legacy)
        }

        // Filtre par statut
        if (this.filterStatus !== 'ALL') {
          list = list.filter(l => this.getLoanStatus(l) === this.filterStatus)
        }

        // Recherche textuelle
        const q = (this.search || '').toLowerCase().trim()
        if (q) {
          list = list.filter(l => {
            const username = (l.client_username || '').toLowerCase()
            const amount = String(l.loan_amount || l.target_amount || '')
            const operator = (l.operator || '').toLowerCase()
            const phone = (l.phone_number || '')
            const name = (l.name || '').toLowerCase()
            const transOp = (l.transaction_operator || '').toLowerCase()
            return username.includes(q) || amount.includes(q) || operator.includes(q) ||
                   phone.includes(q) || name.includes(q) || transOp.includes(q)
          })
        }

        this.filteredLoans = list
        console.log('📊 ' + this.filteredLoans.length + ' prêts affichés')
        this.currentPage = 1
      } catch (e) {
        console.error('Erreur lors du filtrage local :', e)
        this.filteredLoans = []
      }
    },

    debouncedSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => this.filterLoansLocally(), 300)
    },

    updateTabCounts() {
      if (!Array.isArray(this.loans)) return
      const newLoans = this.loans.filter(l => !l.is_legacy)
      this.tabs.forEach(tab => {
        if (tab.value === 'ALL') {
          tab.count = 0
        } else {
          tab.count = newLoans.filter(l => this.getLoanStatus(l) === tab.value).length
        }
      })
    },

    // ========== GESTION DU DETAIL ==========
    openDetail(loan) {
      if (!loan) return
      this.selectedLoan = JSON.parse(JSON.stringify(loan))
      this.managerNote = ''
      this.detailDrawer = true
    },

    // ========== ACTIONS ==========
    quickAction(loan, action) {
      if (!loan || loan.is_legacy) return
      this.confirmLoan = loan
      this.confirmAction = action
      this.selectedLoan = JSON.parse(JSON.stringify(loan))
      this.managerNote = ''
      this.confirmDialog = true
    },

    processAction(action) {
      if (!this.selectedLoan || this.selectedLoan.is_legacy) return
      this.confirmLoan = this.selectedLoan
      this.confirmAction = action
      this.confirmDialog = true
    },

    async confirmProcess() {
      if (this.actionLoading || !this.confirmLoan || this.confirmLoan.is_legacy) return
      this.actionLoading = true
      try {
        await axios.post('/api/v1/manager/loans/' + this.confirmLoan.id + '/action/', {
          action: this.confirmAction,
          note: this.managerNote
        })
        this.notify('Prêt ' + this.confirmActionTitle.toLowerCase() + ' avec succès', 'success')
        this.confirmDialog = false
        this.detailDrawer = false
        await this.fetchLoans()
      } catch (e) {
        let msg = 'Action échouée'
        if (e.response && e.response.data && e.response.data.error) {
          msg = e.response.data.error
        }
        this.notify(msg, 'error')
      } finally {
        this.actionLoading = false
      }
    },

    // ========== UTILS ==========
    notify(msg, color = 'success') {
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', color)
      this.$store.commit('activateSnackBar', true)
    },

    formatAmount(v) {
      if (v === undefined || v === null || v === '') return '—'
      const num = Number(v)
      if (isNaN(num)) return '—'
      return new Intl.NumberFormat('fr-FR').format(num)
    },

    formatDate(d) {
      if (!d) return '—'
      try {
        return new Date(d).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (e) {
        return '—'
      }
    },

    formatDateShort(d) {
      if (!d) return '—'
      try {
        return new Date(d).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: 'short'
        })
      } catch (e) {
        return '—'
      }
    },

    formatCriterion(key) {
      if (!key) return ''
      const labels = {
        completed_campaigns: 'Campaigns',
        active_investment: 'Investment',
        business_account: 'Business',
        affiliate_count: 'Affiliates',
        business_active_clients: 'Biz Clients'
      }
      if (key.indexOf('monthly_group_') === 0) return 'Njangi Group'
      return labels[key] || key
    },

    parsePercentage(percentageStr) {
      if (!percentageStr) return 0
      const num = parseInt(String(percentageStr).replace('%', ''))
      return isNaN(num) ? 0 : num
    }
  }
}
</script>

<style scoped>
.loans-manager {
  background: #f5f5f5;
  min-height: 100vh;
}

.header-card {
  border-radius: 12px !important;
}

.stat-card {
  border-radius: 12px !important;
  transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.15) !important;
}

.stat-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #616161;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1.2;
}

.search-input >>> .v-input__slot {
  border-radius: 8px !important;
}

.loan-card {
  border-radius: 12px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: 2px solid transparent;
  position: relative;
}

.loan-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0,0,0,0.15) !important;
  border-color: #1976d2;
}

.legacy-card {
  border-left: 4px solid #757575 !important;
}

.legacy-card:hover {
  border-color: #757575 !important;
}

.pending-pulse {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(255,152,0,0.3);
  }
  50% {
    box-shadow: 0 4px 16px rgba(255,152,0,0.6);
  }
}

.legacy-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  font-weight: 700;
  font-size: 0.65rem;
}

.card-header {
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
}

.status-chip {
  font-weight: 600 !important;
  letter-spacing: 0.5px;
}

.amount-section {
  background: #fafafa;
  border-radius: 8px;
  padding: 12px;
}

.legacy-section {
  background: #fafafa;
  border-radius: 8px;
  padding: 12px;
  border-left: 4px solid #757575;
}

.info-grid {
  margin-top: 8px;
}

.info-item {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  color: #616161;
}

.detail-drawer {
  box-shadow: -8px 0 24px rgba(0,0,0,0.15) !important;
}

.drawer-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  position: relative;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.purpose-text {
  background: #f5f5f5;
  border-left: 4px solid #1976d2;
  border-radius: 4px;
  padding: 12px;
  font-size: 0.9rem;
  line-height: 1.6;
  color: #424242;
}

.action-buttons {
  position: sticky;
  bottom: 0;
  background: white;
  padding: 16px 0;
  margin: 0 -24px -24px -24px;
  padding: 16px 24px;
  box-shadow: 0 -4px 12px rgba(0,0,0,0.08);
}

@media (max-width: 960px) {
  .stat-value {
    font-size: 1.5rem;
  }

  .detail-drawer {
    width: 100% !important;
    max-width: 100% !important;
  }
}
</style>
